import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { SportsThemedButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { LoadingIndicatorModule } from '@frontend/sports/loading-indicator-feature';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { PlainLinkComponent } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { EffectsModule } from '@ngrx/effects';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ArcModule } from 'packages/sports/web/app/src/arc/arc.module';
import { RaceViewBestOddsBadgeComponent } from 'packages/sports/web/app/src/best-odds-guarantee/race-view-best-odds-badge.component';
import { TranslationToStringPipe } from 'packages/sports/web/app/src/cds/translation.pipe';
import { ShowInEpcotDirective } from 'packages/sports/web/app/src/common/epcot-config.directive';
import { EpcotNotificationComponent } from 'packages/sports/web/app/src/common/epcotNotifications/notification.component';
import { MyBetsBaseModule } from 'packages/sports/web/app/src/my-bets-base/my-bets-base.module';
import { NumpadModule } from 'packages/sports/web/app/src/numpad/numpad.module';
import { StateStorageModule } from 'packages/sports/web/app/src/store-persist/state-storage.module';

import BetslipBaseModule from '../../base/betslip-base.module';
import BetslipDigitalComboBetModule from '../combo-bet/betslip-digital-combo-bet.module';
import { BetslipDigitalCommonModule } from '../common/digital-module';
import { NotificationsModule } from '../notifications/module';
import BetslipDigitalPicksModule from '../picks/betslip-digital-picks.module';
import BetslipPicksModule from '../picks/betslip-picks.module';
import PickOddsModule from '../picks/pick-odds.module';
import { QuickDepositModule } from '../quick-deposit/quick-deposit.module';
import BetslipDigitalResultModule from '../result/betslip-digital-result.module';
import BetslipDigitalTaxationModule from '../taxation/betslip-digital-taxation.module';
import BetslipDigitalValidationModule from '../validation/betslip-digital-validation.module';
import { EditBetActions } from './actions';
import EditBetAddPickContainerComponent from './components/add-picks/edit-bet-add-pick-container.component';
import EditBetAddPickComponent from './components/add-picks/edit-bet-add-pick.component';
import { EditBetAddPicksContainerDesktopComponent } from './components/add-picks/edit-bet-add-picks-container-desktop.component';
import { EditBetAddPicksContainerMobileComponent } from './components/add-picks/edit-bet-add-picks-container-mobile.component';
import { EditBetAddSelectionComponent } from './components/add-picks/edit-bet-add-selection.component';
import { EditMyBetBottomMessageComponent } from './components/bottom-message/edit-bet-bottom-message.component';
import { EditMybetContainerComponent } from './components/edit-bet-container.component';
import { EditBetErrorsComponent } from './components/edit-bet-errors.component';
import { EditMybetNotificationComponent } from './components/edit-bet-notification.component';
import { EditMybetComponent } from './components/edit-bet.component';
import { EditMybetHeaderComponent } from './components/header/edit-bet-header.component';
import { EditBetOnBoardingComponent } from './components/onboarding/edit-bet-onboarding.component';
import EditBetPickContainerComponent from './components/picks/edit-bet-pick-container.component';
import EditBetPickComponent from './components/picks/edit-bet-pick.component';
import { BetslipEditBetPlaceButtonComponent } from './components/place-button/edit-bet-place-button.component';
import { EditMybetResultHeaderComponent } from './components/result/edit-bet-result-header.component';
import { EditMybetSummaryComponent } from './components/summary/edit-bet-summary.component';
import { BetslipEditBetEarlyPayoutSubscriptionEffects } from './effects/early-payout-subscription.effects';
import { BetslipEditMybetEffects } from './effects/effects';
import { BetslipEditBetPlaceEffects } from './effects/place-bet.effects';
import PushEffects from './effects/push-effects';
import { BetslipTrackingEffects } from './effects/tracking.effects';
import { EditBetOnBoardingService } from './services/betslip-onboarding.service';
import { BetslipSourcePageService } from './services/betslip-source-page.service';
import { EditBetAddSelectionInfoDialogService } from './services/dialogs/edit-bet-add-selection-info-dialog.service';
import { EditBetBetLostDialogService } from './services/dialogs/edit-bet-bet-lost-dialog.service';
import { EditBetBetSettledDialogService } from './services/dialogs/edit-bet-bet-settled-dialog.service';
import { EditBetBetWonDialogService } from './services/dialogs/edit-bet-bet-won-dialog.service';
import { EditBetExitConfirmationDialogService } from './services/dialogs/edit-bet-exit-confirmation-dialog.service';
import { EditBetPicksAddedInfoDialogService } from './services/dialogs/edit-bet-picks-added-info-dialog.service';
import { EditBetV2PickAddConfirmationDialogService } from './services/dialogs/edit-bet-v2-pick-add-confirmation-dialog.service';
import { EarlyPayoutResponseProcessorService } from './services/early-payout-response-processor.service';
import { EditBetAddPicksContainerOffsetProvider } from './services/edit-bet-add-picks-container-offset.provider';
import { EditBetCheckingService } from './services/edit-bet-checking.service';
import { EditBetConvertersService } from './services/edit-bet-converters.service';
import { EditBetEarlyPayoutSubscriptionServiceFactory } from './services/edit-bet-early-payout-subscription.service.factory';
import { EditBetErrorService } from './services/edit-bet-error.service';
import { EditBetModalDialogService } from './services/edit-bet-modal-dialog.service';
import { EditBetPickRefreshService } from './services/edit-bet-pick-refresh.service';
import { EditBetPlaceButtonStateService } from './services/edit-bet-place-button-state.service';
import { EditBetPlaceService } from './services/edit-bet-place.service';
import { EditBetRequestService } from './services/edit-bet-request.service';
import { EditBetTimeoutProvider } from './services/edit-bet-timeout.provider';
import { EditBetUiChangeHandler } from './services/edit-bet-ui-change-handler';
import { EditBetUtils } from './services/edit-bet-utils';

@NgModule({
    imports: [
        BetslipDigitalPicksModule,
        BetslipBaseModule,
        BetslipDigitalCommonModule,
        BetslipDigitalResultModule,
        StateStorageModule,
        PlainLinkComponent,
        LoadingIndicatorModule,
        TranslationToStringPipe,
        ArcModule,
        MyBetsBaseModule,
        CommonModule,
        NgScrollbarModule,
        EffectsModule.forFeature([
            BetslipEditBetEarlyPayoutSubscriptionEffects,
            BetslipEditBetPlaceEffects,
            BetslipTrackingEffects,
            BetslipEditMybetEffects,
            PushEffects,
        ]),
        RaceViewBestOddsBadgeComponent,
        BetslipDigitalComboBetModule,
        BetslipPicksModule,
        NumpadModule,
        BetslipDigitalTaxationModule,
        NotificationsModule,
        BetslipDigitalValidationModule,
        QuickDepositModule,
        PickOddsModule,
        AutomationModule,
        ShowInEpcotDirective,
        EpcotNotificationComponent,
        TrustPipe,
        DsButton,
        DsButtonIcon,
        IconCustomComponent,
        SportsThemedButtonDirective,
    ],
    declarations: [
        EditMybetResultHeaderComponent,
        EditBetOnBoardingComponent,
        BetslipEditBetPlaceButtonComponent,
        EditBetAddSelectionComponent,
        EditMybetContainerComponent,
        EditMybetHeaderComponent,
        EditMybetSummaryComponent,
        EditMybetComponent,
        EditBetAddPicksContainerDesktopComponent,
        EditBetPickComponent,
        EditBetPickContainerComponent,
        EditBetAddPickContainerComponent,
        EditBetAddPickComponent,
        EditBetErrorsComponent,
        EditMyBetBottomMessageComponent,
        EditMybetNotificationComponent,
        EditBetAddPicksContainerMobileComponent,
    ],
    exports: [],
    providers: [
        EditBetUtils,
        EditBetAddSelectionInfoDialogService,
        EditBetBetLostDialogService,
        EditBetBetSettledDialogService,
        EditBetBetWonDialogService,
        EditBetExitConfirmationDialogService,
        EditBetPicksAddedInfoDialogService,
        EditBetV2PickAddConfirmationDialogService,
        EditBetEarlyPayoutSubscriptionServiceFactory,
        EditBetOnBoardingService,
        BetslipSourcePageService,
        EarlyPayoutResponseProcessorService,
        EditBetModalDialogService,
        EditBetPickRefreshService,
        EditBetPlaceButtonStateService,
        EditBetAddPicksContainerOffsetProvider,
        EditBetCheckingService,
        EditBetConvertersService,
        EditBetPlaceService,
        EditBetTimeoutProvider,
        EditBetRequestService,
        EditBetUiChangeHandler,
        EditBetErrorService,
    ],
})
export default class EditBetModule {
    static readonly EditBetActions = EditBetActions;

    /**
     *
     */
    constructor(editBetUiChangeHandler: EditBetUiChangeHandler) {
        editBetUiChangeHandler.onAppInit();
    }
}
